import React from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { Link, useHistory } from 'react-router-dom';
import SearchPager from 'shared_components/generate_pager';
import moment from 'moment';
import { GLOBAL_TABLE_PAGE_SIZE } from 'global_config/constant-global';
import { TABLE_HEADER_STYLE, TABLE_CELL_STYLE, TABLE_PAGINATION_STYLE, TABLE_SORT_LABEL_STYLE } from 'utils/tableStyles';

const { useState, useEffect } = React;

function SearchReportTable(props) {

    const history = useHistory();
    const { data, latestGeneratedId } = props;
    const [pageSize, setPageSize] = useState(GLOBAL_TABLE_PAGE_SIZE);
    const [page, setPage] = useState(0);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_date');
    const [tblLoading, setTblLoading] = useState(true);

    // Reset to first page when data changes (e.g., report type changes)
    useEffect(() => {
        setPage(0);
    }, [data.length]);

    // Focus on the newly generated file by navigating to its page
    useEffect(() => {
        if (latestGeneratedId && data.length > 0) {
            const index = data.findIndex(item => item.generated_document_id === latestGeneratedId);
            if (index !== -1) {
                const newPage = Math.floor(index / pageSize);
                setPage(newPage);
            }
        }
    }, [latestGeneratedId, data, pageSize]);

    const dataSource = data.map(rec=>({
        key : rec.generated_document_id,
        generated_document_id: rec.generated_document_id,
        document_id: rec.document_id,
        file_location: rec.file_location,
        file_name: rec.file_name,
        parameters_used: rec.parameters_used,
        status: rec.status,
        last_updated_by: rec.last_updated_by,
        last_updated_date: rec.last_updated_date,
        created_by: rec.created_by,
        created_date: rec.created_date
    }));

    useState(()=> {
        if(dataSource){
            setTblLoading(false);
        }
    });

    const columns = [
        { id: 'file_name', label: 'FILENAME', minWidth: 120 },
        { id: 'parameters_used', label: 'PARAMETERS USED', minWidth: 200 },
        { id: 'created_date', label: 'DATE GENERATED', minWidth: 120 },
        { id: 'operation', label: 'OPERATION', minWidth: 100 }
    ];

    const handleDownload = async (record) => {
        window.open(`${process.env.REACT_APP_BACKEND_API}reports/download/${record.generated_document_id}`);
    }

    const handleChangeSize = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function descendingComparator(a, b, orderBy) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    function getComparator(order, orderBy) {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    function stableSort(array, comparator) {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }
    
    return (
        <div>
            <SearchPager
                pageTotal={dataSource.length === null ? 0 : dataSource.length}
                pageSize={pageSize}
                handleChangeSize={handleChangeSize}
            />
            <TableContainer component={Paper}>
                <Table stickyHeader aria-label="search report table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    style={{ 
                                        ...TABLE_HEADER_STYLE,
                                        minWidth: column.minWidth, 
                                        textTransform: 'uppercase' 
                                    }}
                                    sortDirection={orderBy === column.id ? order : false}
                                >
                                    {['file_name', 'parameters_used', 'created_date'].includes(column.id) ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={(e) => handleRequestSort(e, column.id)}
                                            style={TABLE_SORT_LABEL_STYLE}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stableSort(dataSource, getComparator(order, orderBy))
                            .slice(page * pageSize, page * pageSize + pageSize)
                            .map((row) => (
                                <TableRow hover tabIndex={-1} key={row.key}>
                                    <TableCell style={TABLE_CELL_STYLE}>{row.file_name}</TableCell>
                                    <TableCell style={TABLE_CELL_STYLE}>{row.parameters_used}</TableCell>
                                    <TableCell style={TABLE_CELL_STYLE}>{row.created_date}</TableCell>
                                    <TableCell style={TABLE_CELL_STYLE}>
                                        <a onClick={() => handleDownload(row)} style={{ textTransform: 'capitalize', cursor: 'pointer' }}>
                                            DOWNLOAD
                                        </a>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[]}
                    component="div"
                    count={dataSource.length}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={(event) => handleChangeSize(parseInt(event.target.value, 10))}
                    style={TABLE_PAGINATION_STYLE}
                />
            </TableContainer>
        </div>
    );
}

export default SearchReportTable;